"use client";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import type { AccessibilityStatement } from "../../types/statement";
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";
import { HelpText } from "../help-text";

interface YourEffortsStepProps {
  data: AccessibilityStatement["efforts"];
  onChange: (
    data: AccessibilityStatement["efforts"],
    changedField?: keyof AccessibilityStatement["efforts"]
  ) => void;
}

const effortOptions = [
  "Accessibility is part of our mission statement.",
  "Accessibility is part of our internal policies.",
  "Accessibility is integrated into our procurement practices.",
  "An accessibility officer or official has been appointed.",
  "Continual accessibility training is provided for employees.",
  "Clear accessibility targets and responsibilities exist within the organisation.",
  "Formal accessibility quality assurance methods exist within the organisation.",
];

export function YourEffortsStep({ data, onChange }: YourEffortsStepProps) {
  const handleEffortToggle = (effort: string, checked: boolean) => {
    const updatedEfforts = checked
      ? [...data.selectedEfforts, effort]
      : data.selectedEfforts.filter((e) => e !== effort);

    onChange({ ...data, selectedEfforts: updatedEfforts }, "selectedEfforts");
  };

  const handleAdditionalEffortChange = (index: number, value: string) => {
    const updatedAdditionalEfforts = [...data.additionalEfforts];
    updatedAdditionalEfforts[index] = value;
    onChange(
      { ...data, additionalEfforts: updatedAdditionalEfforts },
      "additionalEfforts"
    );
  };

  const addAdditionalEffort = () => {
    if (data.additionalEfforts.length < 5) {
      onChange(
        { ...data, additionalEfforts: [...data.additionalEfforts, ""] },
        "additionalEfforts"
      );
    }
  };

  return (
    <div className="cy:space-y-6">
      <div>
        <h2 className="cy:text-[28px] cy:font-semibold cy:mb-2">
          Your efforts
        </h2>
        <p className="cy:text-base! cy:flow-root! cy:gap-0! cy:mt-10">
          Select any current actions your company or organisation is taking to
          ensure web accessibility.
          <TooltipWrapper className="cy:ml-1" content="This helps users understand your commitment and builds trust in your statement." />
        </p>
      </div>
      <div className="cy:space-y-3">
        {effortOptions.map((effort, index) => (
          <div
            key={index}
            className="cy:flex cy:items-start cy:space-x-3"
          >
            <Checkbox
              id={`effort-${index}`}
              checked={data.selectedEfforts.includes(effort)}
              onCheckedChange={(checked) =>
                handleEffortToggle(effort, checked as boolean)
              }
              className="cy:mr-3! cy:rounded-[4px]!"
            />
            <Label
              htmlFor={`effort-${index}`}
              className="cy:text-base cy:font-normal! cy:mb-1! cy:-mt-1"
            >
              {effort}
            </Label>
          </div>
        ))}
      </div>

      <div className="cy:space-y-4">
        {data.additionalEfforts.map((effort, index) => (
          <div key={index}>
            <Label
              htmlFor={`additional-${index}`}
              className="cy:text-lg cy:font-semibold"
            >
              Additional efforts {index + 1}
            </Label>
            {index === 0 && (
              <HelpText>
                Example: We include people with disabilities in our user testing
                processes.
              </HelpText>
            )}
            <Input
              id={`additional-${index}`}
              value={effort}
              onChange={(e) =>
                handleAdditionalEffortChange(index, e.target.value)
              }
              className="cy:mt-1"
            />
          </div>
        ))}

        {data.additionalEfforts.length < 5 && (
          <button
            type="button"
            onClick={addAdditionalEffort}
            className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-base cy:font-normal cy:text-slate-800 cy:focus:outline-none"
          >
            <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
              <Plus className="cy:w-3 cy:h-3" />
            </span>
            Additional efforts
          </button>
        )}
      </div>
    </div>
  );
}
